import React, { useCallback } from 'react'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useQueryClient } from 'react-query'
import { useFiles, useBedFiles } from 'src/api/file'
import { startCarrierAnalysis } from 'src/api/analysis/annotate'
import { refusalTextOf } from 'src/services/analysisStart'
import { useFileActions } from '../FileUploadView/hooks/useFileActions'
import { useStableFilesData } from '../FileUploadView/hooks/useStableFilesData'
import { BATCHES_QUERY_KEY } from '../Batches/useBatches'
import AnnotationView from './AnnotationView'
import { GlobalScrollbarStyles } from './AnnotationView/styles/StyledComponents'
import { ANALYSIS_STATS_KEY } from './useAnalysisStats'

// The analyses the New analysis panel does not cover (Multisample, cohort
// creation) still run through the existing wizard. This wraps it, unchanged, in a
// large dialog, with the start handling Home used before the panel replaced the
// wizard there. The wizard reports the charge and a short balance on its own
// result screen, so nothing else is shown here.
//
// Props: open, onClose, onStarted(result) (optional, after any successful start).

const noop = () => {}

const WizardHost = ({ onStarted }) => {
  const filesApi = useFiles()
  const { data: bedFiles = [] } = useBedFiles().query
  const { data: files, optimisticUpdates } = useStableFilesData(filesApi.query)
  const { handleFileAnnotation } = useFileActions(filesApi, optimisticUpdates)
  const queryClient = useQueryClient()

  const started = useCallback(
    (result) => {
      queryClient.invalidateQueries('all-analysis')
      queryClient.invalidateQueries(ANALYSIS_STATS_KEY)
      queryClient.invalidateQueries(BATCHES_QUERY_KEY)
      if (onStarted) onStarted(result)
    },
    [queryClient, onStarted],
  )

  const handleAnnotateSubmit = useCallback(
    async (selectedFile, annotation) => {
      if (annotation?.annotation_type === 'CARRIER') {
        try {
          const result = await startCarrierAnalysis(annotation)
          if (result && !result.error) started(result)
          return result
        } catch (error) {
          console.error('Carrier analysis failed:', error)
          const refused = refusalTextOf(error)
          return refused ? { error: refused, refused: true } : { error: 'Unknown error' }
        }
      }

      if (!selectedFile) {
        console.error('No file selected for annotation.')
        return false
      }

      const result = await handleFileAnnotation(selectedFile, annotation, noop, noop, noop, noop, noop)
      if (result && !result.error) started(result)
      return result
    },
    [handleFileAnnotation, started],
  )

  return (
    <GlobalScrollbarStyles>
      <AnnotationView files={files || []} bedFiles={bedFiles} onAnnotate={handleAnnotateSubmit} />
    </GlobalScrollbarStyles>
  )
}

const OtherAnalysisTypesDialog = ({ open, onClose, onStarted }) => {
  const queryClient = useQueryClient()
  // Multisample and cohort starts call their APIs directly, not onAnnotate, so the
  // lists catch up when the dialog closes.
  const handleClose = useCallback(() => {
    queryClient.invalidateQueries('all-analysis')
    queryClient.invalidateQueries(ANALYSIS_STATS_KEY)
    onClose()
  }, [queryClient, onClose])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="other-analysis-types-title"
      // Anchored to the top, so the wizard grows downward between steps instead
      // of the dialog jumping around the vertical centre.
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{ sx: { borderRadius: '12px', mt: { xs: 2, md: 6 } } }}
    >
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, pr: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography id="other-analysis-types-title" variant="h6" component="h2">
            Other analysis types
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Multisample analysis and cohort creation
          </Typography>
        </Box>
        <Tooltip title="Close" arrow>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      {/* Mounted only while open, so the file list is fetched only when needed. */}
      <DialogContent sx={{ pt: 1, pb: 3 }}>{open && <WizardHost onStarted={onStarted} />}</DialogContent>
    </Dialog>
  )
}

export default OtherAnalysisTypesDialog
